import { INavLinkGroup, Nav, PivotItem, Position } from '@fluentui/react';
import AppContext from 'App/AppContext';
import {
  IPCheckbox,
  IPLabel,
  IPPivot,
  IPPivotItem,
  IPSeparator,
  IPSpinButton,
  IPSpinner,
  IPText,
} from 'baseComponents';
import { IPComboBox } from 'baseComponents/Combobox/IPComboBox';
import { IPDropdown } from 'baseComponents/Dropdown/IPDropdown';
import { IPSpinnerSize } from 'baseComponents/Spinner/IPSpinner';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { IPSelectableOption } from 'baseComponents/Utils/SelectableOption';
import OutOfScopeDialog from 'components/Control/OutOfScopeDialog';
import GenericModal from 'components/Dialogs/GenericModal';
import WarningMessage from 'components/Notification/WarningMessage';
import AuthSchemaPicker from 'components/Pickers/AuthSchemaPicker';
import KeyValueTagPicker from 'components/Pickers/KeyValueTagPicker';
import LocalizedDatePicker from 'components/Pickers/LocalizedDatePicker';
import { OwnerPicker } from 'components/Pickers/OwnerPicker';
import TimePicker from 'components/Pickers/TimePicker';
import UserRoleGroupPicker from 'components/Pickers/UserRoleGroupPicker';
import { KeyValueTag } from 'components/Tags/KeyValueTag';
import { TaskAssignment } from 'components/Tasks/TaskTypes/Details/TaskAssignment';
import { dropdownStyles } from 'components/Tasks/TaskTypes/Details/TaskDetailStyles';
import RichTextReadOnly from 'components/Text/RichTextReadOnly';
import { getEntityStatusTextList } from 'globalFunctions';
import {
  globalComboContainerHeight,
  globalStackTokensGapMedium,
  globalStackTokensGapSmall,
  globalTextStylesError,
  groupIcon,
  guestIcon,
  permissionsIcon,
  standardsIcon,
  tagIcon,
  taskStartIcon,
  userIcon,
  workflowIcon,
} from 'globalStyles';
import Joi from 'joi';
import { AuthSchema } from 'models/auth/authSchema';
import Control, { ApplicabilityReasons } from 'models/control';
import { EntityStatus } from 'models/entityStatus';
import Group from 'models/group';
import Norm from 'models/norm';
import { getObjectiveStatusTextList, ObjectiveStatus } from 'models/objective/objective';
import { IOwner, Owner } from 'models/owner';
import { getRiskStatusTextList, getTaskStatusTextList } from 'models/riskState';
import Tag from 'models/tag';
import { default as Task } from 'models/tasks/task';
import { FormEvent, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiCreateTag } from 'services/Api/tagService';
import { apiRequest } from 'services/Auth/authConfig';
import { FeatureTypes, hasUserFeature } from 'services/Auth/featurePermissions';
import { getLocalizedMessageOptions } from 'services/Localization/joiValidation';
import AppError from 'utils/appError';
import { setNewDateKeepTime, setNewTimeKeepDate } from 'utils/datetime';

export enum BulkActionType {
  None = 0,
  Tag = 1,
  Owner = 2,
  Group = 3,
  Assignee = 4,
  StartDate = 5,
  Status = 6,
  Standards = 7,
  Permissions = 8,
  ObjectiveStatus = 9,
  Applicability = 10,
  RiskStatus = 11,
  TaskStatus = 12,
  KPIType = 13,
}

//
// Actions
//
export interface IBulkAction {
  actionType: BulkActionType;
}

export interface IBulkActionTag extends IBulkAction {
  tagsToAdd: Tag[];
  tagsToRemove: Tag[];
}

export interface IBulkActionOwner extends IBulkAction {
  newOwner: Owner | undefined;
}

export interface IBulkActionGroup extends IBulkAction {
  newGroup: Group | undefined;
}

export interface IBulkActionAssignee extends IBulkAction {
  newAuthSchemaId: number | undefined;
  newAssigneeId: string | undefined;
}

export interface IBulkActionStatus extends IBulkAction {
  newStatus: EntityStatus;
}

export interface IBulkActionObjectiveStatus extends IBulkAction {
  newStatus: ObjectiveStatus;
}

export interface IBulkActionStandards extends IBulkAction {
  normsToAdd: Norm[];
  normsToRemove: Norm[];
}

export interface IBulkActionPermissions extends IBulkAction {
  newPermission: AuthSchema | undefined;
}

export interface IBulkActionApplicability extends IBulkAction {
  newApplicabilities: ApplicabilityReasons[] | undefined;
  newOutOfScopeReason: string | undefined;
}

export interface IBulkActionRiskStatus extends IBulkAction {
  newStatus: number | undefined;
}

export interface IBulkActionTaskStatus extends IBulkAction {
  newStatus: number | undefined;
}

export interface IBulkActionKPIType extends IBulkAction {
  isField: boolean;
}

export interface IBulkActionTaskStartDate extends IBulkAction {
  newStartDate: Date | undefined;
  newStartTime: Date | undefined;
  newEndTime: Date | undefined;
  newDeadline: Date | undefined;
  newDeadlineTime: Date | undefined;
  moveStartDirectionKey: string;
  moveStartDays: number;
  moveDeadlineDuration: number;
  operationType: 'set' | 'move';
}

//
// Action states
//
export interface IBulkActionState {}

export interface IBulkActionStateTags extends IBulkActionState {
  tags: Tag[];
  checkboxState: ICheckboxState[];
}

export interface IBulkActionStateStandards extends IBulkActionState {
  checkboxState: ICheckboxState[];
}

export enum ICheckboxState {
  unchecked = 0,
  checked = 1,
  indetermined = 2,
}

//
// Component state
//
interface IBulkActionsModalProps {
  title: string;
  itemCount: number;
  noAuthCount: number;
  isOpen: boolean;
  actionTypes: BulkActionType[];
  disableRemoveOwner?: boolean;
  disallowOwnerRoles?: boolean;
  onClose: () => void;
  onExecute: (actions: IBulkAction[]) => void;
  onGetState?: (actionType: BulkActionType) => Promise<IBulkActionState | undefined>;
}

const BulkActionsModal = (props: IBulkActionsModalProps) => {
  const { t } = useTranslation(['translation']);
  const appContext = useContext(AppContext);
  const [selectedAction, setSelectedAction] = useState<BulkActionType | undefined>(undefined);
  const [selectedTagsToAdd, setSelectedTagsToAdd] = useState<Tag[]>([]);
  const [selectedTagsToRemove, setSelectedTagsToRemove] = useState<Tag[]>([]);
  const [selectedNewOwner, setSelectedNewOwner] = useState<IOwner | undefined>(undefined);
  const [removeOwner, setRemoveOwner] = useState<boolean>(false);
  const [selectedNewGroup, setSelectedNewGroup] = useState<Group | undefined>(undefined);
  const [removeGroup, setRemoveGroup] = useState<boolean>(false);
  const [dummyTask, setDummyTask] = useState<Task>(new Task());
  const [removeAssignee, setRemoveAssignee] = useState<boolean>(false);
  const [selectedStatus, setSelectedStatus] = useState<EntityStatus | undefined>(undefined);
  const [selectedObjectiveStatus, setSelectedObjectiveStatus] = useState<ObjectiveStatus | undefined>(undefined);
  const [selectedApplicability, setSelectedApplicability] = useState<ApplicabilityReasons[] | undefined>(undefined);
  const [currentStates, setCurrentStates] = useState<Record<number, IBulkActionState>>({ 0: {} });
  const [selectedStandardsToAdd, setSelectedStandardsToAdd] = useState<Norm[]>([]);
  const [selectedStandardsToRemove, setSelectedStandardsToRemove] = useState<Norm[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedNewPermission, setSelectedNewPermission] = useState<AuthSchema | undefined>(undefined);
  const [removePermission, setRemovePermission] = useState<boolean>(false);
  const [showOutOfScopeDialog, setShowOutOfScopeDialog] = useState<boolean>(false);
  const [selectedOutOfScopeReason, setSelectedOutOfScopeReason] = useState<string | undefined>(undefined);
  const [selectedRiskStatus, setSelectedRiskStatus] = useState<number | undefined>(undefined);
  const [selectedTaskStatus, setSelectedTaskStatus] = useState<number | undefined>(undefined);
  const [selectedIsField, setSelectedIsField] = useState<boolean | undefined>(undefined);
  const [selectedNewStartDate, setSelectedNewStartDate] = useState<Date | undefined>(undefined);
  const [selectedNewStartTime, setSelectedNewStartTime] = useState<Date | undefined>(undefined);
  const [selectedNewEndTime, setSelectedNewEndTime] = useState<Date | undefined>(undefined);
  const [selectedNewDeadline, setSelectedNewDeadline] = useState<Date | undefined>(undefined);
  const [selectedNewDeadlineTime, setSelectedNewDeadlineTime] = useState<Date | undefined>(undefined);
  const [moveStartDirectionKey, setMoveStartDirectionKey] = useState<string>('forward');
  const [moveStartDays, setMoveStartDays] = useState<number>(0);
  const [timeUnit, setTimeUnit] = useState<string>('days');
  const [moveDeadlineDuration, setMoveDeadlineDuration] = useState<number>(0);
  const [operationType, setOperationType] = useState<'set' | 'move'>('set');

  useEffect(() => {
    if (props.isOpen) {
      const groups = getNavGroups();
      if (groups[0].links.length === 1) {
        loadSelectedAction(Number.parseInt(groups[0].links[0].key ?? '0'));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.isOpen]);

  const getNavGroups = (): INavLinkGroup[] => {
    const navLinks: INavLinkGroup[] = [];

    const mainGroup: INavLinkGroup = { links: [] };

    if (props.actionTypes.includes(BulkActionType.Tag)) {
      mainGroup.links.push(
        ...[
          {
            name: t('translation:BulkActionsModal.Actions.Tags'),
            url: '',
            key: BulkActionType.Tag.toString(),
            icon: tagIcon.iconName,
            onclick: () => setSelectedAction(BulkActionType.Tag),
          },
        ],
      );
    }

    if (props.actionTypes.includes(BulkActionType.Owner)) {
      mainGroup.links.push(
        ...[
          {
            name: t('translation:BulkActionsModal.Actions.Owner'),
            url: '',
            key: BulkActionType.Owner.toString(),
            icon: guestIcon.iconName,
            onclick: () => setSelectedAction(BulkActionType.Owner),
          },
        ],
      );
    }

    if (props.actionTypes.includes(BulkActionType.Assignee)) {
      mainGroup.links.push(
        ...[
          {
            name: t('translation:BulkActionsModal.Actions.Assignee'),
            url: '',
            key: BulkActionType.Assignee.toString(),
            icon: userIcon.iconName,
            onclick: () => setSelectedAction(BulkActionType.Assignee),
          },
        ],
      );
    }

    if (props.actionTypes.includes(BulkActionType.StartDate)) {
      mainGroup.links.push(
        ...[
          {
            name: t('translation:BulkActionsModal.Actions.StartDate'),
            url: '',
            key: BulkActionType.StartDate.toString(),
            icon: taskStartIcon.iconName,
            onclick: () => setSelectedAction(BulkActionType.StartDate),
          },
        ],
      );
    }

    if (props.actionTypes.includes(BulkActionType.Status)) {
      mainGroup.links.push(
        ...[
          {
            name: t('translation:BulkActionsModal.Actions.Status'),
            url: '',
            key: BulkActionType.Status.toString(),
            icon: workflowIcon.iconName,
            onclick: () => setSelectedAction(BulkActionType.Status),
          },
        ],
      );
    }

    if (props.actionTypes.includes(BulkActionType.ObjectiveStatus)) {
      mainGroup.links.push(
        ...[
          {
            name: t('translation:BulkActionsModal.Actions.Status'),
            url: '',
            key: BulkActionType.ObjectiveStatus.toString(),
            icon: workflowIcon.iconName,
            onclick: () => setSelectedAction(BulkActionType.ObjectiveStatus),
          },
        ],
      );
    }

    if (props.actionTypes.includes(BulkActionType.RiskStatus)) {
      mainGroup.links.push(
        ...[
          {
            name: t('translation:BulkActionsModal.Actions.Status'),
            url: '',
            key: BulkActionType.RiskStatus.toString(),
            icon: workflowIcon.iconName,
            onclick: () => setSelectedAction(BulkActionType.RiskStatus),
          },
        ],
      );
    }

    if (props.actionTypes.includes(BulkActionType.TaskStatus)) {
      mainGroup.links.push(
        ...[
          {
            name: t('translation:BulkActionsModal.Actions.TaskStatus'),
            url: '',
            key: BulkActionType.TaskStatus.toString(),
            icon: 'FavoriteStarFill',
            onclick: () => setSelectedAction(BulkActionType.TaskStatus),
          },
        ],
      );
    }

    if (props.actionTypes.includes(BulkActionType.KPIType)) {
      mainGroup.links.push(
        ...[
          {
            name: t('translation:BulkActionsModal.Actions.KPIType'),
            url: '',
            key: BulkActionType.KPIType.toString(),
            icon: 'FieldEmpty',
            onclick: () => loadSelectedAction(BulkActionType.KPIType),
          },
        ],
      );
    }

    if (props.actionTypes.includes(BulkActionType.Applicability)) {
      mainGroup.links.push(
        ...[
          {
            name: t('translation:BulkActionsModal.Actions.Applicability'),
            url: '',
            key: BulkActionType.Applicability.toString(),
            icon: workflowIcon.iconName,
            onclick: () => setSelectedAction(BulkActionType.Applicability),
          },
        ],
      );
    }

    if (props.actionTypes.includes(BulkActionType.Standards)) {
      mainGroup.links.push(
        ...[
          {
            name: t('translation:BulkActionsModal.Actions.Standards'),
            url: '',
            key: BulkActionType.Standards.toString(),
            icon: standardsIcon.iconName,
            onclick: () => setSelectedAction(BulkActionType.Standards),
          },
        ],
      );
    }

    if (props.actionTypes.includes(BulkActionType.Group)) {
      mainGroup.links.push(
        ...[
          {
            name: t('translation:BulkActionsModal.Actions.Group'),
            url: '',
            key: BulkActionType.Group.toString(),
            icon: groupIcon.iconName,
            onclick: () => setSelectedAction(BulkActionType.Group),
          },
        ],
      );
    }

    if (props.actionTypes.includes(BulkActionType.Permissions) && hasUserFeature(appContext, FeatureTypes.RBAC)) {
      mainGroup.links.push(
        ...[
          {
            name: t('translation:BulkActionsModal.Actions.Permissions'),
            url: '',
            key: BulkActionType.Permissions.toString(),
            icon: permissionsIcon.iconName,
            onclick: () => setSelectedAction(BulkActionType.Permissions),
          },
        ],
      );
    }

    navLinks.push(mainGroup);

    return navLinks;
  };

  //
  // Actions
  //
  const getActionContent = (
    action: BulkActionType | undefined,
    states: Record<number, IBulkActionState>,
  ): JSX.Element | null => {
    try {
      if (!action) return null;
      let content: JSX.Element | null;

      switch (action) {
        case BulkActionType.Tag:
          content = getActionContentTag(states);
          break;
        case BulkActionType.Owner:
          content = getActionContentOwner();
          break;
        case BulkActionType.Group:
          content = getActionContentGroup();
          break;
        case BulkActionType.Assignee:
          content = getActionContentAssignee();
          break;
        case BulkActionType.Status:
          content = getActionContentStatus();
          break;
        case BulkActionType.ObjectiveStatus:
          content = getActionContentObjectiveStatus();
          break;
        case BulkActionType.Standards:
          content = getActionContentStandards(states);
          break;
        case BulkActionType.Permissions:
          content = getActionContentPermissions();
          break;
        case BulkActionType.Applicability:
          content = getActionContentApplicability();
          break;
        case BulkActionType.RiskStatus:
          content = getActionContentRiskStatus();
          break;
        case BulkActionType.TaskStatus:
          content = getActionContentTaskStatus();
          break;
        case BulkActionType.KPIType:
          content = getActionContentKPIType();
          break;
        case BulkActionType.StartDate:
          content = getActionContentStartDate();
          break;
        default:
          throw new AppError('Not implemented');
      }

      if (content) {
        return content;
      } else {
        throw new AppError('State not provided');
      }
    } catch (err) {
      const appErr = err as AppError;

      return <IPText styles={globalTextStylesError}>{appErr.message}</IPText>;
    }
  };

  const getActionContentStandards = (states: Record<number, IBulkActionState>): JSX.Element | null => {
    const state = states[BulkActionType.Standards] as IBulkActionStateStandards;
    if (!state || !state.checkboxState || state.checkboxState.length < appContext.globalDataCache.norms.items.length) {
      return null;
    }

    return (
      <IPStack verticalFill tokens={globalStackTokensGapSmall}>
        <IPStack.Item>
          <IPLabel>{t('translation:BulkActionsModal.Standards.Label')}</IPLabel>
        </IPStack.Item>
        <IPStack.Item>
          <IPStack tokens={globalStackTokensGapSmall}>
            {appContext.globalDataCache.norms.items.map((norm, index) => {
              return (
                <IPStack.Item key={norm.normId}>
                  <IPCheckbox
                    label={norm.name}
                    defaultChecked={state.checkboxState[index] === ICheckboxState.checked}
                    defaultIndeterminate={state.checkboxState[index] === ICheckboxState.indetermined}
                    onChange={(ev, checked) => {
                      const newState = checked ?? false;

                      if (newState && !selectedStandardsToAdd.some((n) => n.normId === norm.normId)) {
                        if (selectedStandardsToRemove.some((n) => n.normId === norm.normId)) {
                          setSelectedStandardsToRemove(
                            selectedStandardsToRemove.filter((n) => n.normId !== norm.normId),
                          );
                        }
                        const newStandardsToAdd = [...selectedStandardsToAdd, norm];
                        setSelectedStandardsToAdd(newStandardsToAdd);
                      } else if (!newState && !selectedStandardsToRemove.some((n) => n.normId === norm.normId)) {
                        if (selectedStandardsToAdd.some((n) => n.normId === norm.normId)) {
                          setSelectedStandardsToAdd(selectedStandardsToAdd.filter((n) => n.normId !== norm.normId));
                        }
                        const newStandardsToRemove = [...selectedStandardsToRemove, norm];
                        setSelectedStandardsToRemove(newStandardsToRemove);
                      }
                    }}
                  />
                </IPStack.Item>
              );
            })}
          </IPStack>
        </IPStack.Item>
      </IPStack>
    );
  };

  const getActionContentStatus = (): JSX.Element => {
    return (
      <IPStack verticalFill tokens={globalStackTokensGapMedium}>
        <IPStack.Item>
          <IPLabel>{t('translation:BulkActionsModal.Status.Label')}</IPLabel>
          <IPComboBox
            selectedKey={selectedStatus}
            options={getEntityStatusTextList(t)}
            onChange={(ev, option) => {
              setSelectedStatus(option?.key as EntityStatus);
            }}
            allowFreeform={false}
            styles={globalComboContainerHeight}
            autoComplete="on"
            useComboBoxAsMenuWidth={true}
          />
        </IPStack.Item>
      </IPStack>
    );
  };

  const getActionContentObjectiveStatus = (): JSX.Element => {
    return (
      <IPStack verticalFill tokens={globalStackTokensGapMedium}>
        <IPStack.Item>
          <IPLabel>{t('translation:BulkActionsModal.Status.Label')}</IPLabel>
          <IPComboBox
            selectedKey={selectedObjectiveStatus}
            options={getObjectiveStatusTextList(t)}
            onChange={(ev, option) => {
              setSelectedObjectiveStatus(option?.key as ObjectiveStatus);
            }}
            allowFreeform={false}
            styles={globalComboContainerHeight}
            autoComplete="on"
            useComboBoxAsMenuWidth={true}
          />
        </IPStack.Item>
      </IPStack>
    );
  };

  const getActionContentRiskStatus = (): JSX.Element => {
    return (
      <IPStack verticalFill tokens={globalStackTokensGapMedium}>
        <IPStack.Item>
          <IPLabel>{t('translation:BulkActionsModal.Status.Label')}</IPLabel>
          <IPComboBox
            selectedKey={selectedRiskStatus}
            options={getRiskStatusTextList(appContext)}
            onChange={(ev, option) => {
              setSelectedRiskStatus(option?.key as number);
            }}
            allowFreeform={false}
            styles={globalComboContainerHeight}
            autoComplete="on"
            useComboBoxAsMenuWidth={true}
          />
        </IPStack.Item>
      </IPStack>
    );
  };

  const getActionContentTaskStatus = (): JSX.Element => {
    return (
      <IPStack verticalFill tokens={globalStackTokensGapMedium}>
        <IPStack.Item>
          <IPLabel>{t('translation:BulkActionsModal.Status.Label')}</IPLabel>
          <IPComboBox
            selectedKey={selectedTaskStatus}
            options={getTaskStatusTextList(appContext)}
            onChange={(ev, option) => {
              setSelectedTaskStatus(option?.key as number);
            }}
            allowFreeform={false}
            autoComplete="on"
            styles={globalComboContainerHeight}
            useComboBoxAsMenuWidth={true}
          />
        </IPStack.Item>
      </IPStack>
    );
  };

  const getActionContentKPIType = (): JSX.Element => {
    return (
      <IPStack verticalFill tokens={globalStackTokensGapMedium}>
        <IPStack.Item>
          <IPCheckbox
            label={t('translation:BulkActionsModal.KPIType.PromoteFieldToKPI')}
            checked={selectedIsField === false}
            onChange={(ev, checked) => setSelectedIsField(checked === true ? false : true)}
          />
        </IPStack.Item>
      </IPStack>
    );
  };

  const updateApplicability = (
    event: FormEvent<HTMLDivElement>,
    option?: IPSelectableOption<ApplicabilityReasons> | undefined,
    index?: number | undefined,
  ) => {
    if (option) {
      let newApplicability = [...(selectedApplicability ?? [])];
      if ((option.key as ApplicabilityReasons) === ApplicabilityReasons.OutOfScope) {
        setShowOutOfScopeDialog(true);
      } else {
        //remove not applicable
        newApplicability = newApplicability.filter((c) => c !== ApplicabilityReasons.OutOfScope);
        setSelectedOutOfScopeReason(undefined);
        //add/remove selected options
        if (option.selected) {
          newApplicability.push(option.key as ApplicabilityReasons);
        } else {
          newApplicability = newApplicability.filter((c) => c !== (option.key as ApplicabilityReasons));
        }

        setSelectedApplicability(newApplicability);
      }
    }
  };

  const setOutOfScope = (reason: string | undefined) => {
    setSelectedApplicability([ApplicabilityReasons.OutOfScope]);
    setSelectedOutOfScopeReason(reason);
  };

  const getActionContentApplicability = (): JSX.Element => {
    return (
      <IPStack verticalFill tokens={globalStackTokensGapMedium}>
        <IPStack.Item>
          <IPDropdown
            label={t('control:TabDetails.Fields.Applicability.Label')}
            selectedKeys={selectedApplicability}
            multiSelect
            options={Control.getApplicabilityReasonList(t)}
            onChange={updateApplicability}
            styles={{ root: { minWidth: 200 }, listbox: { maxHeight: 300 } }}
          />
        </IPStack.Item>
        {selectedOutOfScopeReason && (
          <IPStack.Item>
            <RichTextReadOnly>{selectedOutOfScopeReason}</RichTextReadOnly>
          </IPStack.Item>
        )}
      </IPStack>
    );
  };

  const getActionContentTag = (states: Record<number, IBulkActionState>): JSX.Element | null => {
    const state = states[BulkActionType.Tag] as IBulkActionStateTags;
    if (!state || !state.checkboxState || !state.tags || state.checkboxState.length !== state.tags.length) {
      return null;
    }

    const addTagToState = (tag: Tag) => {
      const state = states[BulkActionType.Tag] as IBulkActionStateTags;
      const curIdx = state.tags.findIndex((t) => t.tagId === tag.tagId);
      if (curIdx >= 0) {
        if (state.checkboxState[curIdx] !== ICheckboxState.checked) {
          state.checkboxState[curIdx] = ICheckboxState.checked;
          setSelectedTagsToAdd([...selectedTagsToAdd, tag]);
          setSelectedTagsToRemove(selectedTagsToRemove.filter((s) => s.tagId !== tag.tagId));
        }
      } else {
        tag.usedCount = 0;
        state.tags.push(tag);
        state.checkboxState.push(ICheckboxState.checked);
        setSelectedTagsToAdd([...selectedTagsToAdd, tag]);
      }

      const newStates = { ...states };
      newStates[BulkActionType.Tag] = state;
      setCurrentStates(newStates);
    };

    return (
      <IPStack verticalFill tokens={globalStackTokensGapMedium}>
        <IPStack.Item>
          <IPStack>
            {state.tags.map((tag, index) => {
              return (
                <IPStack.Item key={tag.tagId}>
                  <IPStack horizontal verticalAlign="center" tokens={globalStackTokensGapSmall}>
                    <IPStack.Item>
                      <IPCheckbox
                        checked={
                          !selectedTagsToRemove.some((t) => t.tagId === tag.tagId) &&
                          (state.checkboxState[index] === ICheckboxState.checked ||
                            selectedTagsToAdd.some((t) => t.tagId === tag.tagId))
                        }
                        indeterminate={
                          !selectedTagsToRemove.some((t) => t.tagId === tag.tagId) &&
                          !selectedTagsToAdd.some((t) => t.tagId === tag.tagId) &&
                          state.checkboxState[index] === ICheckboxState.indetermined
                        }
                        onChange={(ev, checked) => {
                          if (checked) {
                            setSelectedTagsToAdd([...selectedTagsToAdd, tag]);
                            setSelectedTagsToRemove(selectedTagsToRemove.filter((s) => s.tagId !== tag.tagId));
                          } else {
                            setSelectedTagsToAdd(selectedTagsToAdd.filter((s) => s.tagId !== tag.tagId));
                            setSelectedTagsToRemove([...selectedTagsToRemove, tag]);
                          }
                        }}
                      />
                    </IPStack.Item>
                    <IPStack.Item>
                      <KeyValueTag tag={tag} />
                    </IPStack.Item>
                    <IPStack.Item>
                      <IPText variant="small">
                        {t('translation:General.List.ItemCount', {
                          count: tag.usedCount,
                        })}
                      </IPText>
                    </IPStack.Item>
                  </IPStack>
                </IPStack.Item>
              );
            })}
          </IPStack>
        </IPStack.Item>
        <IPStack.Item>
          <IPLabel>{t('translation:BulkActionsModal.Tag.LabelAdd')}</IPLabel>
          <KeyValueTagPicker
            selectedTags={[]}
            onAdd={(tag) => addTagToState(tag)}
            onRemove={() => {}}
            onCreate={(tag) => addTagToState(tag)}
            isLoading={false}
            allowCreate={true}
          />
        </IPStack.Item>
      </IPStack>
    );
  };

  const getActionContentPermissions = (): JSX.Element => {
    return (
      <IPStack tokens={globalStackTokensGapSmall}>
        <IPStack.Item>
          <AuthSchemaPicker
            label={t('translation:BulkActionsModal.Permissions.LabelSet')}
            selectedAuthSchemaId={selectedNewPermission?.authSchemaId}
            onSelect={(authSchema) => {
              if (!authSchema) {
                setSelectedNewPermission(undefined);
              } else {
                setSelectedNewPermission(authSchema);
              }
            }}
            disabled={removePermission}
          />
        </IPStack.Item>
        <IPStack.Item>
          <IPCheckbox
            label={t('translation:BulkActionsModal.Permissions.LabelRemove')}
            checked={removePermission}
            onChange={(ev, checked) => {
              setRemovePermission(checked ?? false);
              setSelectedNewPermission(undefined);
            }}
          />
        </IPStack.Item>
      </IPStack>
    );
  };

  const getActionContentOwner = (): JSX.Element => {
    return (
      <IPStack verticalFill tokens={globalStackTokensGapSmall}>
        <IPStack.Item>
          <IPLabel>{t('translation:BulkActionsModal.Owner.LabelSet')}</IPLabel>
          <OwnerPicker
            selectedItem={selectedNewOwner}
            onSelect={(item, owner) => {
              setSelectedNewOwner(owner);
            }}
            disabled={removeOwner}
            hideLabel={true}
            disallowRoles={props.disableRemoveOwner}
          />
        </IPStack.Item>
        {!props.disableRemoveOwner && (
          <IPStack.Item>
            <IPCheckbox
              label={t('translation:BulkActionsModal.Owner.LabelRemove')}
              checked={removeOwner}
              onChange={(ev, checked) => {
                setRemoveOwner(checked ?? false);
                setSelectedNewOwner(undefined);
              }}
            />
          </IPStack.Item>
        )}
      </IPStack>
    );
  };

  const getActionContentAssignee = (): JSX.Element => {
    return (
      <IPStack verticalFill tokens={globalStackTokensGapSmall}>
        <IPStack.Item>
          <TaskAssignment
            orgTask={dummyTask.clone()}
            task={dummyTask}
            canUpdate={undefined}
            onUpdate={setDummyTask}
            disabled={removeAssignee}
          />
        </IPStack.Item>
        <IPStack.Item>
          <IPCheckbox
            label={t('translation:BulkActionsModal.Assignee.LabelRemove')}
            checked={removeAssignee}
            onChange={(ev, checked) => {
              setRemoveAssignee(checked ?? false);
              setDummyTask(new Task());
            }}
          />
        </IPStack.Item>
      </IPStack>
    );
  };

  const getActionContentGroup = (): JSX.Element => {
    return (
      <IPStack verticalFill tokens={globalStackTokensGapSmall}>
        <IPStack.Item>
          <IPLabel>{t('translation:BulkActionsModal.Group.LabelSet')}</IPLabel>
          <UserRoleGroupPicker
            selectedItems={selectedNewGroup ? [selectedNewGroup] : []}
            allowedItems={appContext.globalDataCache.groups.items}
            itemLimit={1}
            onSelect={(items) => {
              setSelectedNewGroup(items[0] as Group);
            }}
            onRemove={() => {
              setSelectedNewGroup(undefined);
            }}
            disabled={removeGroup}
            allowUsers={false}
            allowGroups={true}
            allowRoles={false}
          />
        </IPStack.Item>
        <IPStack.Item>
          <IPCheckbox
            label={t('translation:BulkActionsModal.Group.LabelRemove')}
            checked={removeGroup}
            onChange={(ev, checked) => {
              setRemoveGroup(checked ?? false);
              setSelectedNewGroup(undefined);
            }}
          />
        </IPStack.Item>
      </IPStack>
    );
  };

  const getTaskStartDateErrorMessage = (fieldName: string): string | undefined => {
    const localizedFields = {
      startDateTime: t('task:Start.Label'),
      endDateTime: t('task:End.ErrorMessage'),
      deadline: t('task:Deadline.ErrorMessage'),
    };

    const schema = Joi.object({
      startDateTime: Joi.date().optional().label(localizedFields['startDateTime']),
      endDateTime: Joi.date().optional().min(Joi.ref('startDateTime')).label(localizedFields['endDateTime']),
      deadline: Joi.date().optional().min(Joi.ref('endDateTime')).label(localizedFields['deadline']),
    }).prefs(getLocalizedMessageOptions());

    const objToValidate = {
      startDateTime: selectedNewStartDate
        ? setNewTimeKeepDate(
            selectedNewStartDate,
            selectedNewStartTime?.getHours() ?? 0,
            selectedNewStartTime?.getMinutes() ?? 0,
          )
        : undefined,
      endDateTime: selectedNewStartDate
        ? setNewTimeKeepDate(
            selectedNewStartDate,
            selectedNewEndTime?.getHours() ?? selectedNewStartTime?.getHours() ?? 0,
            selectedNewEndTime?.getMinutes() ?? selectedNewStartTime?.getMinutes() ?? 0,
          )
        : undefined,
      deadline: selectedNewDeadline
        ? setNewTimeKeepDate(
            selectedNewDeadline,
            selectedNewDeadlineTime?.getHours() ?? 0,
            selectedNewDeadlineTime?.getMinutes() ?? 0,
          )
        : undefined,
    };

    const result = schema.validate(objToValidate, { abortEarly: false });
    const fieldError = result.error?.details?.find((e) => {
      return e.context?.key === fieldName || e.message.toLowerCase().includes(fieldName.toLowerCase());
    });

    return fieldError ? fieldError.message : undefined;
  };

  const getForwardBackwardOptions = (): IPSelectableOption<string>[] => {
    return [
      { key: 'forward', text: t('translation:BulkActionsModal.StartDate.Direction.Forward') },
      { key: 'backward', text: t('translation:BulkActionsModal.StartDate.Direction.Backward') },
    ];
  };

  const durationTimeOptions: IPSelectableOption[] = [
    {
      key: 'hours',
      text: t('task:Repeat.Hours'),
    },
    {
      key: 'days',
      text: t('task:Repeat.Days'),
    },
  ];

  const getActionContentStartDate = (): JSX.Element => {
    return (
      <IPPivot
        selectedKey={operationType}
        onLinkClick={(item) => setOperationType(item?.props.itemKey as 'set' | 'move')}
      >
        <IPPivotItem itemKey="set" headerText={t('translation:BulkActionsModal.StartDate.Pivot.Set')}>
          <IPStack verticalFill tokens={globalStackTokensGapSmall} styles={{ root: { padding: 10 } }}>
            <IPStack horizontal tokens={globalStackTokensGapSmall}>
              <IPStack.Item grow>
                <LocalizedDatePicker
                  label={t('translation:BulkActionsModal.StartDate.LabelStart')}
                  showGoToToday={true}
                  showWeekNumbers={true}
                  value={selectedNewStartDate}
                  onDateChange={(newDate) => {
                    if (!newDate) {
                      setSelectedNewStartDate(undefined);
                      setSelectedNewDeadline(undefined);
                    } else {
                      setSelectedNewStartDate(setNewDateKeepTime(newDate, selectedNewStartDate));
                    }
                  }}
                  allowEmptyValue={true}
                />
                <IPText variant="small" styles={globalTextStylesError}>
                  {getTaskStartDateErrorMessage('endDateTime')}
                </IPText>
                <IPText variant="small" styles={globalTextStylesError}>
                  {getTaskStartDateErrorMessage('startDateTime')}
                </IPText>
              </IPStack.Item>
              <IPStack.Item>
                <TimePicker
                  selectedTime={selectedNewStartTime}
                  label={t('translation:BulkActionsModal.StartDate.LabelStartTime')}
                  onUpdateTime={(hours: number, mins: number) => {
                    setSelectedNewStartTime(setNewTimeKeepDate(selectedNewStartTime, hours, mins));
                  }}
                  allowEmptyValue={true}
                  onClearTime={() => {
                    setSelectedNewStartTime(undefined);
                    setSelectedNewEndTime(undefined);
                    setSelectedNewDeadlineTime(undefined);
                  }}
                />
              </IPStack.Item>
              <IPStack.Item>
                <TimePicker
                  selectedTime={selectedNewEndTime}
                  label={t('translation:BulkActionsModal.StartDate.LabelEndTime')}
                  onUpdateTime={(hours: number, mins: number) => {
                    setSelectedNewEndTime(setNewTimeKeepDate(selectedNewEndTime, hours, mins));
                  }}
                  allowEmptyValue={true}
                  onClearTime={() => {
                    setSelectedNewEndTime(undefined);
                    setSelectedNewDeadlineTime(undefined);
                  }}
                  disabled={selectedNewStartTime === undefined}
                />
              </IPStack.Item>
            </IPStack>
            <IPStack horizontal tokens={globalStackTokensGapSmall}>
              <IPStack.Item grow>
                <LocalizedDatePicker
                  label={t('translation:BulkActionsModal.StartDate.LabelDeadline')}
                  showGoToToday={true}
                  showWeekNumbers={true}
                  value={selectedNewDeadline}
                  onDateChange={(newDate) => {
                    if (!newDate) {
                      setSelectedNewDeadline(undefined);
                    } else {
                      setSelectedNewDeadline(setNewDateKeepTime(newDate, selectedNewStartDate));
                    }
                  }}
                  allowEmptyValue={true}
                  disabled={selectedNewStartDate === undefined}
                />
                <IPText variant="small" styles={globalTextStylesError}>
                  {getTaskStartDateErrorMessage('deadline')}
                </IPText>
              </IPStack.Item>
              <IPStack.Item>
                <TimePicker
                  selectedTime={selectedNewDeadlineTime}
                  label={t('translation:BulkActionsModal.StartDate.LabelDeadlineTime')}
                  onUpdateTime={(hours: number, mins: number) => {
                    setSelectedNewDeadlineTime(setNewTimeKeepDate(selectedNewStartTime, hours, mins));
                  }}
                  allowEmptyValue={true}
                  onClearTime={() => {
                    setSelectedNewDeadlineTime(undefined);
                  }}
                  //disable the deadline time picker if end time is not set and start date and deadline are the same day
                  //when this is true, we cannot calculate the deadline time correctly because it must be after the end time of the start date
                  disabled={selectedNewEndTime === undefined}
                />
              </IPStack.Item>
            </IPStack>
          </IPStack>
        </IPPivotItem>
        <PivotItem itemKey="move" headerText={t('translation:BulkActionsModal.StartDate.Pivot.Move')}>
          <IPStack verticalFill tokens={globalStackTokensGapSmall} styles={{ root: { padding: 10 } }}>
            <IPStack horizontal tokens={globalStackTokensGapSmall}>
              <IPStack.Item grow>
                <IPDropdown
                  selectedKey={moveStartDirectionKey}
                  onChange={(ev, option) => setMoveStartDirectionKey((option?.key as string) ?? 'forward')}
                  options={getForwardBackwardOptions()}
                  label={t('translation:BulkActionsModal.StartDate.LabelStart')}
                  styles={{ root: { minWidth: 200 } }}
                />
              </IPStack.Item>
              <IPStack.Item grow>
                <IPSpinButton
                  labelPosition={Position.top}
                  label={t('translation:BulkActionsModal.StartDate.LabelDays')}
                  styles={{ root: { minWidth: 200 } }}
                  min={0}
                  max={3 * 365}
                  step={1}
                  value={moveStartDays.toString()}
                  onChange={(ev, newValue) => setMoveStartDays(Number.parseInt(newValue ?? ''))}
                />
              </IPStack.Item>
            </IPStack>
          </IPStack>
          <IPStack verticalFill tokens={globalStackTokensGapSmall} styles={{ root: { padding: 10 } }}>
            <IPStack horizontal tokens={globalStackTokensGapSmall} verticalAlign="end">
              <IPStack.Item grow>
                <IPSpinButton
                  labelPosition={Position.top}
                  label={t('translation:BulkActionsModal.StartDate.LabelDuration')}
                  styles={{ root: { minWidth: 200 } }}
                  min={0}
                  max={24 * 365}
                  step={1}
                  value={
                    timeUnit !== 'hours'
                      ? Math.floor(moveDeadlineDuration / 24).toString()
                      : moveDeadlineDuration.toString()
                  }
                  onChange={(ev, newValue) => {
                    let newTimeValue = Number.parseInt(newValue ?? '');
                    if (timeUnit !== 'hours') {
                      newTimeValue = newTimeValue * 24;
                    }
                    setMoveDeadlineDuration(newTimeValue);
                  }}
                />
              </IPStack.Item>
              <IPStack.Item>
                <IPDropdown
                  options={durationTimeOptions}
                  styles={dropdownStyles}
                  selectedKey={timeUnit}
                  onChange={(event: React.FormEvent<HTMLDivElement>, option?: IPSelectableOption, index?: number) => {
                    if (!option) return;
                    const newTimeUnit = option?.key.toString();
                    setTimeUnit(newTimeUnit);
                    if (newTimeUnit === timeUnit) return;
                    let newDuration;

                    if (newTimeUnit !== 'hours') {
                      newDuration = moveDeadlineDuration * 24;
                    } else {
                      newDuration = Math.floor(moveDeadlineDuration / 24);
                    }

                    setMoveDeadlineDuration(newDuration);
                  }}
                />
              </IPStack.Item>
            </IPStack>
          </IPStack>
        </PivotItem>
      </IPPivot>
    );
  };

  const getActions = (): IBulkAction[] => {
    const newActions: IBulkAction[] = [];

    if (selectedTagsToAdd.length > 0 || selectedTagsToRemove.length > 0) {
      const newAction: IBulkActionTag = {
        actionType: BulkActionType.Tag,
        tagsToAdd: selectedTagsToAdd,
        tagsToRemove: selectedTagsToRemove,
      };

      newActions.push(newAction);
    }

    if (selectedNewOwner || removeOwner) {
      const newAction: IBulkActionOwner = {
        actionType: BulkActionType.Owner,
        newOwner: new Owner(selectedNewOwner, appContext),
      };

      newActions.push(newAction);
    }

    if (selectedNewGroup || removeGroup) {
      const newAction: IBulkActionGroup = {
        actionType: BulkActionType.Group,
        newGroup: selectedNewGroup,
      };

      newActions.push(newAction);
    }

    if (dummyTask.authSchemaId || dummyTask.userId || removeAssignee) {
      const newAction: IBulkActionAssignee = {
        actionType: BulkActionType.Assignee,
        newAuthSchemaId: dummyTask.authSchemaId,
        newAssigneeId: dummyTask.userId,
      };

      newActions.push(newAction);
    }

    if (selectedStatus !== undefined) {
      const newAction: IBulkActionStatus = {
        actionType: BulkActionType.Status,
        newStatus: selectedStatus,
      };

      newActions.push(newAction);
    }

    if (selectedObjectiveStatus !== undefined) {
      const newAction: IBulkActionObjectiveStatus = {
        actionType: BulkActionType.ObjectiveStatus,
        newStatus: selectedObjectiveStatus,
      };

      newActions.push(newAction);
    }

    if (selectedApplicability !== undefined) {
      const newAction: IBulkActionApplicability = {
        actionType: BulkActionType.Applicability,
        newApplicabilities: selectedApplicability,
        newOutOfScopeReason: selectedOutOfScopeReason,
      };

      newActions.push(newAction);
    }

    if (selectedStandardsToAdd.length > 0 || selectedStandardsToRemove.length > 0) {
      const newAction: IBulkActionStandards = {
        actionType: BulkActionType.Standards,
        normsToAdd: selectedStandardsToAdd,
        normsToRemove: selectedStandardsToRemove,
      };

      newActions.push(newAction);
    }

    if (selectedNewPermission || removePermission) {
      const newAction: IBulkActionPermissions = {
        actionType: BulkActionType.Permissions,
        newPermission: selectedNewPermission,
      };

      newActions.push(newAction);
    }

    if (selectedRiskStatus) {
      const newAction: IBulkActionRiskStatus = {
        actionType: BulkActionType.RiskStatus,
        newStatus: selectedRiskStatus,
      };

      newActions.push(newAction);
    }

    if (selectedTaskStatus) {
      const newAction: IBulkActionTaskStatus = {
        actionType: BulkActionType.TaskStatus,
        newStatus: selectedTaskStatus,
      };

      newActions.push(newAction);
    }

    if (selectedIsField !== undefined) {
      const newAction: IBulkActionKPIType = {
        actionType: BulkActionType.KPIType,
        isField: selectedIsField,
      };

      newActions.push(newAction);
    }

    if (
      (operationType === 'set' && (selectedNewStartDate || selectedNewStartTime)) ||
      (operationType === 'move' && (moveStartDays > 0 || moveDeadlineDuration > 0))
    ) {
      const newAction: IBulkActionTaskStartDate = {
        actionType: BulkActionType.StartDate,
        operationType: operationType,
        newStartDate: selectedNewStartDate,
        newStartTime: selectedNewStartTime,
        newEndTime: selectedNewEndTime,
        newDeadline: selectedNewDeadline,
        newDeadlineTime: selectedNewDeadlineTime,
        moveStartDirectionKey: moveStartDirectionKey,
        moveStartDays: moveStartDays,
        moveDeadlineDuration: moveDeadlineDuration,
      };

      newActions.push(newAction);
    }

    return newActions;
  };

  const preExecuteActions = async (actions: IBulkAction[]): Promise<IBulkAction[]> => {
    //add new tags
    const action = actions.find((a) => a.actionType === BulkActionType.Tag);
    if (action) {
      const actionTag = action as IBulkActionTag;
      if (actionTag.tagsToAdd) {
        for (let idx = 0; idx < actionTag.tagsToAdd.length; idx++) {
          const tag = actionTag.tagsToAdd[idx];
          if (tag.tagId < 0) {
            const accessToken = await appContext.getAccessToken(apiRequest.scopes);
            const newTag = await apiCreateTag(tag, accessToken);
            actionTag.tagsToAdd[idx] = newTag;
            appContext.globalDataCache.tags.add(newTag);
          }
        }
      }
    }

    return actions;
  };

  const onExecute = async () => {
    try {
      let newActions: IBulkAction[] = getActions();
      newActions = await preExecuteActions(newActions);
      props.onExecute(newActions);
    } catch (err) {
      appContext.setError(err);
    }
  };

  const hasActions = (): boolean => {
    return getActions().length > 0;
  };

  const loadSelectedAction = async (actionType: BulkActionType) => {
    try {
      if (props.onGetState && !currentStates[actionType]) {
        setIsLoading(true);
        const newState = await props.onGetState(actionType);
        if (newState) {
          const newStates = { ...currentStates };
          newStates[actionType] = newState;
          setCurrentStates(newStates);
        }
      }

      if (actionType === BulkActionType.KPIType && selectedIsField === undefined) {
        setSelectedIsField(false);
      }

      setSelectedAction(actionType);
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsLoading(false);
    }
  };

  const getSubTitle = (): string => {
    return t('translation:BulkActionsModal.SubTitle', { count: props.itemCount - props.noAuthCount });
  };

  //
  // Main render
  //
  return (
    <GenericModal
      isOpen={props.isOpen}
      onClose={props.onClose}
      onSave={onExecute}
      keepOpenAfterSave={true}
      saveDisabled={!hasActions()}
      saveButtonText={t('translation:General.Button.Execute')}
      title={props.title}
      subTitle={getSubTitle()}
      height={700}
      width={800}
      maxWidth={'min(90vw, 800px)'}
      maxHeight={'90vh'}
      minHeight={300}
      minWidth={300}
    >
      <IPStack verticalFill>
        <IPStack.Item>
          <IPSeparator />
        </IPStack.Item>
        {props.noAuthCount > 0 && (
          <IPStack.Item styles={{ root: { paddingBottom: 10 } }}>
            <WarningMessage message={t('translation:BulkActionsModal.SubTitleNoAuth', { count: props.noAuthCount })} />
          </IPStack.Item>
        )}
        <IPStack.Item grow>
          <IPStack horizontal tokens={globalStackTokensGapMedium}>
            <IPStack.Item styles={{ root: { width: 200, minWidth: appContext.isMobileView ? undefined : 200 } }}>
              <Nav
                selectedKey={selectedAction?.toString()}
                groups={getNavGroups()}
                onLinkClick={(ev, item) => {
                  const actionKey = Number.parseInt(item?.key ?? '0');
                  loadSelectedAction(actionKey as BulkActionType);
                }}
              />
            </IPStack.Item>
            <IPSeparator vertical />
            {isLoading && (
              <IPStack.Item grow>
                <IPStack verticalAlign="center" horizontalAlign="center">
                  <IPSpinner size={IPSpinnerSize.large} />
                </IPStack>
              </IPStack.Item>
            )}
            {!isLoading && <IPStack.Item grow>{getActionContent(selectedAction, currentStates)}</IPStack.Item>}
          </IPStack>
        </IPStack.Item>
      </IPStack>
      <OutOfScopeDialog
        isActionPending={false}
        reason={selectedOutOfScopeReason}
        isOutOfScope={selectedApplicability?.includes(ApplicabilityReasons.OutOfScope)}
        isOpen={showOutOfScopeDialog}
        dismiss={() => setShowOutOfScopeDialog(false)}
        setOutOfScope={setOutOfScope}
      ></OutOfScopeDialog>
    </GenericModal>
  );
};

export default BulkActionsModal;
